// Solana: the open's intent hash, from the program's `OpenedEvent` log (when an RPC is configured)
// or from the api's index of the wallet's intents.

import type { IntentRow } from "./api";

/** gum-intent program id (scripts/config.yml / the IDL); `DEMO_SOLANA_PROGRAM` names another. */
export const GUM_PROGRAM = "5fBKBpcpdzmJ4dVNZyuhAXyLZ3JnuTiK7Pt22c4WdtJp";
/** sha256("event:OpenedEvent")[..8]; pinned against the IDL in the tests. */
export const OPENED_DISCRIMINATOR = "0112e83ad0640dfc";

const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");

/**
 * `OpenedEvent { user: Pubkey, escrow_id: u64, intent_hash: [u8; 32], … }` from a transaction's log
 * lines, taking only `Program data:` emitted inside gum-intent's own frame (another program in the
 * same transaction could log a look-alike).
 */
export function openedFromLogs(logs: string[], program = GUM_PROGRAM): { intentHash: string; escrowId: bigint } | null {
  const frames: string[] = [];
  for (const line of logs) {
    const m = /^Program (\S+) (invoke \[\d+\]|success|failed.*)$/.exec(line);
    if (m) {
      if (m[2]!.startsWith("invoke")) frames.push(m[1]!);
      else frames.pop();
      continue;
    }
    const d = /^Program data: (\S+)/.exec(line);
    if (!d || frames.at(-1) !== program) continue;
    let b: Uint8Array;
    try {
      b = Uint8Array.from(atob(d[1]!), (c) => c.charCodeAt(0));
    } catch {
      continue;
    }
    if (b.length < 8 + 32 + 8 + 32 || hex(b.subarray(0, 8)) !== OPENED_DISCRIMINATOR) continue;
    const escrowId = new DataView(b.buffer, b.byteOffset + 40, 8).getBigUint64(0, true);
    return { intentHash: `0x${hex(b.subarray(48, 80))}`, escrowId };
  }
  return null;
}

/** Logs of a confirmed transaction through a public RPC (none configured → null). */
export async function txLogs(rpc: string | undefined, sig: string): Promise<string[] | null> {
  if (!rpc) return null;
  const r = await fetch(rpc, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "getTransaction", params: [sig, { commitment: "confirmed", maxSupportedTransactionVersion: 255, encoding: "json" }] }),
  }).catch(() => null);
  const j = (await r?.json().catch(() => null)) as any;
  return j?.result?.meta?.logMessages ?? null;
}

/** The api row this open created, matched by its transaction id. */
export function rowForOpenTx(rows: IntentRow[], sig: string): IntentRow | undefined {
  return rows.find((r) => r.open_tx === sig);
}

/**
 * What `/build` asks a Solana wallet to sign: the plain open (`tx_base64`, a legacy transaction),
 * or a hosted swap's `txs` (v0) — one transaction, or two that land as one Jito bundle.
 */
export function builtTxs(tx: { tx_base64?: string; txs?: string[]; bundle?: boolean }): { txs: string[]; bundle: boolean; versioned: boolean } {
  if (tx.txs?.length) return { txs: tx.txs, bundle: tx.bundle ?? tx.txs.length > 1, versioned: true };
  if (tx.tx_base64) return { txs: [tx.tx_base64], bundle: false, versioned: false };
  throw new Error("the api built no Solana transaction");
}
